import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { ESectionTitle } from 'src/model/enum/sic-ania/section.title.enum';
import {
  IAtrcAttestati,
  IAtrcDetailResponse,
  IAtrcListingResponse,
} from 'src/model/responses/sic-ania/atrc.response';
import {
  ISitaLastResponse,
  ISitaListingResponse,
} from 'src/model/responses/sic-ania/sita.response';
import {
  ISiviDetailResponse,
  ISiviEmission,
  ISiviListingResponse,
} from 'src/model/responses/sic-ania/sivi.response';
import {
  IAtrcDropdown,
  IDetailData,
  ISectionData,
  ISitaDropdown,
  ISiviDropdown,
} from '../interfaces/frontend.data.interface';
import {
  ISitaLastMappedResponse,
  ISitaSummaryMappedResponse,
} from '../interfaces/sita.interface';
import {
  ISiviDetailMappedResponse,
  ISiviSummaryMappedResponse,
} from '../interfaces/sivi.interface';
import { UtilityService } from 'src/api/utility.service';
import { DashboardService } from 'src/api/dashboard.service';
import {
  LABEL_KEY,
  NO_FILTER,
  PLATE_TYPES,
  VALUE_KEY,
  VECHICLE_TYPES,
} from 'src/model/constants/search.constants';
import {
  EButtonVariants,
  EFormFieldSize,
  EInputTypes,
} from 'src/model/enum/sic-ania/form-fields.enum';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  IDropdownConfig,
  IInputConfig,
} from '../interfaces/form-fields.interface';
import { HttpErrorResponse } from '@angular/common/http';
import {
  atrcSectionRows,
  atrcSectionSummary,
  sitaCoverageDetailsRows,
  sitaSectionSummary,
  siviSectionSummary,
} from 'src/model/constants/constants';
import {
  IAtrcDetailMappedResponse,
  IAtrcSummaryMappedResponse,
} from '../interfaces/atrc.interface';
import { IFinalListingRequest } from 'src/model/requests/sic-ania/final-listing.request';
import { IFinalListingResponse } from '../interfaces/search.interface';
import { IErrorResponse } from 'src/model/responses/sic-ania/error.response';
import {
  emptyAtrc,
  emptySita,
  emptySivi,
} from 'src/model/constants/empty-summary-response.constants';
import { GERAR_BOX_MAPPINGS } from 'src/model/constants/dashboard.constants';
import { SicAniaValidators } from 'src/app/validators/sic-ania.validators';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  onDestroy$: Subject<any> = new Subject<any>();

  readonly plateTypeOptions = PLATE_TYPES;
  readonly vehicleTypeOptions = VECHICLE_TYPES;
  readonly labelKey = LABEL_KEY;
  readonly valueKey = VALUE_KEY;
  readonly btnType = EButtonVariants;

  openModal = false;
  errorMessages: any[] = [];

  //form
  searchForm = new FormGroup({
    plateType: new FormControl(
      this.plateTypeOptions[0].value,
      Validators.required
    ),
    vehicleType: new FormControl(
      this.vehicleTypeOptions[0].value,
      Validators.required
    ),
    plate: new FormControl('', [Validators.required]),
  }, [SicAniaValidators.searchFormValidator]);

  // FormField configs
  plateTypeConfigs: IDropdownConfig = {
    form: this.searchForm,
    formControlName: 'plateType',
    slots: {
      description: 'DASHBOARD.SEARCH-WIDGET.PLATE-TYPE',
    },
    size: EFormFieldSize.MEDIUM,
    options: this.plateTypeOptions,
    optionLabel: this.labelKey,
    optionValue: this.valueKey,
    placeholder: this.translate.instant('PLACEHOLDERS.DROPDOWN', {
      parameter: 'tipo di targa',
    }),
  };

  vehicleTypeConfigs: IDropdownConfig = {
    form: this.searchForm,
    formControlName: 'vehicleType',
    slots: {
      description: 'DASHBOARD.SEARCH-WIDGET.VEHICLE-TYPE',
    },
    size: EFormFieldSize.MEDIUM,
    options: this.vehicleTypeOptions,
    optionLabel: this.labelKey,
    optionValue: this.valueKey,
    placeholder: this.translate.instant('PLACEHOLDERS.DROPDOWN', {
      parameter: 'tipo di veicolo',
    }),
  };

  plateConfig: IInputConfig = {
    form: this.searchForm,
    formControlName: 'plate',
    slots: {
      description: this.translate.instant('DASHBOARD.SEARCH-WIDGET.PLATE'),
    },
    type: EInputTypes.TEXT,
    placeholder: this.translate.instant('PLACEHOLDERS.TEXT'),
    errorMessage: '',
  };

  searchData?: ISectionData;
  showSummary = false;
  plate?: string;
  updatedAttestati: IAtrcAttestati[] = [];
  sectionDetails?: IDetailData;
  searchParams?: IFinalListingRequest;
  sitaMappedResponse?: ISitaLastMappedResponse;
  @ViewChildren('accordionContainer')
  accordionContainer?: QueryList<ElementRef>;

  constructor(
    public utilityService: UtilityService,
    private translate: TranslateService,
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.getAccordionSectionIdAndScroll();
    this.cdr.detectChanges();
  }

  getAccordionSectionIdAndScroll() {
    this.utilityService.emitSectionTitleSubject.subscribe((divId) => {
      this.accordionContainer?.forEach((element) => {
        if (element.nativeElement.id.includes(divId)) {
          if (divId) this.scrollToAccordionSection(element);
        }
      });
    });
  }

  scrollToAccordionSection(element: ElementRef) {
    const accordionButton = element.nativeElement.querySelector(
      '.grid-accordion__header'
    );
    setTimeout(() => {
      window.scrollTo({
        top: element.nativeElement.offsetTop + accordionButton.scrollHeight,
        behavior: 'smooth',
      });
    }, 50);
  }

  convertDataFormat(
    response: any,
    propArray: string[],
    propType: string,
    showSeconds = false
  ) {
    let obj = {};
    const mappedArray = propArray.map((prop) => {
      let mappedProp;
      switch (propType) {
        case 'date':
          mappedProp = this.utilityService.convertYYYYMMDDInDDMMYYYY(
            response[prop]
          );
          break;
        case 'float':
          mappedProp = this.utilityService.setOneDecimalValueIfIsZero(
            response[prop]
          );
          break;
        case 'stringHour':
          mappedProp = this.utilityService.convertStringNumberInHour(
            response[prop]
          );
          break;
        case 'timestamp':
          mappedProp = this.utilityService.convertStringTimestampToDate(
            response[prop],
            showSeconds
          );
          break;
        default:
      }

      return mappedProp;
    });

    propArray.forEach((prop, index) => {
      obj = {
        ...obj,
        [prop]: mappedArray[index],
      };
    });

    return obj;
  }

  private getNumSinistriUltimiAnni(cardData: any, numAnni: number) {
    let numSinistri = 0;
    const attestati: IAtrcAttestati[] = cardData.attestati;
    if(attestati){
      attestati.forEach((attestato: IAtrcAttestati) => {
        const numAnniTot = Object.keys(attestato).length;
        if (numAnni <= numAnniTot) {
          for (let i = numAnniTot; i >= numAnniTot - numAnni; i--) {
            const numSinStr =
              attestato[('a' + i + 'Numsin') as keyof IAtrcAttestati];
            if (numSinStr !== '-' && !isNaN(parseInt(numSinStr as string))) {
              numSinistri += parseInt(numSinStr as string);
            }
          }
        }
      });
    }

    return numSinistri;
  }

  reset(): void {
    this.searchForm.reset();
    this.searchForm.patchValue({
      [this.plateTypeConfigs.formControlName]: this.plateTypeOptions[0].value,
      [this.vehicleTypeConfigs.formControlName]:
        this.vehicleTypeOptions[0].value,
    });
    this.searchForm.updateValueAndValidity();
    this.plateConfig.errorMessage = '';
    this.showSummary = false;
    this.searchData = undefined;
  }


  async search() {
    this.showSummary = false;
    this.searchData = undefined;
    const user = await this.userService.getUser();
    this.searchForm.markAllAsTouched();
    if (this.searchForm.valid) {
      const formData = this.searchForm.getRawValue();
      if (NO_FILTER.includes(formData.vehicleType || '')) {
        formData.vehicleType = '';
      }
      await this.dashboardService
        .getFinalListingResponse({
          formIdVeic: this.utilityService.removeNumericValue(`${formData['plateType']}`),
          idVeic: this.utilityService.setIdVeic(formData),
          tipoVeicolo: `${formData['vehicleType']}`,
          insTimestamp: new Date().toISOString(),
          idDenOperatore: user ? user['uid'] : '',
          ldapUsername: user ? user['uid'] : '',
          codAgenzia: user?.businessUnitId,
          denUtente: user?.nome.replace('\'',' ') + ' ' + user?.cognome.replace('\'',' '),
        })
        .then((commonResponse: IFinalListingResponse | undefined) => {
          this.errorMessages = [];
          if(!commonResponse){
            return;
          }
          const stringifiedResponse = this.utilityService.parseStringifiedResponseInJSON(commonResponse) as IFinalListingResponse;
          this.utilityService.manageErrors(this.errorMessages,stringifiedResponse);
          this.buildSiviData(
            stringifiedResponse?.output['sivi-elenco'],
            stringifiedResponse?.output['sivi-ultima']
          );
          this.buildSitaData(
            stringifiedResponse?.output['sita-elenco'],
            stringifiedResponse?.output['sita-ultima']
          );
          this.buildAtrcData(
            stringifiedResponse?.output['atrc-elenco'],
            stringifiedResponse?.output['atrc-ultima']
          );
          this.sectionDetails = this.searchData?.detail;
          if (this.errorMessages.length) {
            this.openModal = true;
          }
          this.showSummary = this.errorMessages.length < 6 ? true : false;
        })
        .catch((errorResponse: HttpErrorResponse) => {
          if (errorResponse) {
            this.errorMessages = [{ section: errorResponse.error.errorCode || '', messages: [errorResponse.error.errorMessage || ''] }]
            this.openModal = true;
          }
        });
    }
    this.cdr.detectChanges();
  }

  setValue(value: any, config: any) {
    if(value.target.value && value.target.id === 'plate'){
      value.target.value =  value.target.value.replace(' ','');
    }
   if (!config || !config.formControlName) return;
    const control = this.searchForm.get(config.formControlName);
    control?.setValue(value.target.value);
    control?.markAsTouched();
    control?.markAsDirty();
    control?.updateValueAndValidity();
    this.setErrorMessage(config);
  }

  private buildSiviData(
    siviListingResponse: ISiviListingResponse | undefined,
    siviFinalResponse: ISiviDetailResponse | undefined
  ): void {
    const listingResponse = siviListingResponse as any;
    let finalResponse = siviFinalResponse as any;
    let dataOperazione;
    let oraOperazione;
    if(!listingResponse && !finalResponse){
      return;
    }
    if (!(finalResponse as IErrorResponse).error) {
      const datesPropsToConvert = [
        'dataInizCircCic',
        'dataEmissione',
        'dataImmatricolazione',
        'dataOperazione',
        'dtOmologazione',
        'dataAggOmol',
      ];
      const numberPropsToConvert = [
        'cilindrata',
        'potenza',
        'velmax',
        'lunga',
        'larga',
        'sbalzo',
        'interasse1',
        'interasse2',
        'interasse3',
      ];
      if(listingResponse){
        listingResponse.emissioni?.forEach((emissione:ISiviEmission) => {
          if(emissione.dataEmissione === finalResponse.dataEmissione){
            dataOperazione = emissione.dataOperazione;
            oraOperazione = emissione.oraOperazione;
          }
        })
      }
      finalResponse = {
        ...finalResponse,
        dataOperazione: dataOperazione,
        oraOperazione: oraOperazione,
      }

      const formData = this.searchForm.getRawValue();
      const siviMappedResponse: ISiviDetailMappedResponse =
        this.utilityService.mapCodeToValue({
          ...finalResponse,
          ...this.convertDataFormat(finalResponse, datesPropsToConvert, 'date'),
          ...this.convertDataFormat(
            finalResponse,
            numberPropsToConvert,
            'float'
          ),
          targa: formData.plateType === 'L' ? finalResponse?.idVeicAlt : this.utilityService.setIdVeic(formData),
          idVeicAlt: formData.plateType === 'L' ? formData.plate : finalResponse?.idVeicAlt,
          use: this.utilityService.getUseValue(finalResponse?.codCatuso || ''),
          category: this.utilityService.getCategoryValue(
            finalResponse?.codCatuso || ''
          ),
          nazione: finalResponse?.nazione ? (finalResponse?.nazione === 'E' ? 'Estera' : 'Italiana') : '',
          emissioneCo2: finalResponse.emissioneCo2 ? (
            (parseInt(finalResponse.emissioneCo2.toString() || '') || 0) / 10
          ).toFixed(1) : '',
          tipoVeicoloMCTCLabel: finalResponse?.tipoVeicoloMCTC,
          tipoVeicoloLabel: finalResponse?.tipoVeicolo,
          tpCambio: GERAR_BOX_MAPPINGS.get(finalResponse.tpCambio),
          targaPrecedente: finalResponse?.targaPrec || finalResponse?.targaPrecedente
        });
      this.searchData = {
        ...this.searchData,
        summary: {
          ...this.searchData?.summary,
          [ESectionTitle.SIVI]: this.utilityService.buildSummaryData(
            siviSectionSummary.data,
            siviMappedResponse
          ) as ISiviSummaryMappedResponse,
        },
        detail: {
          ...this.searchData?.detail,
          [ESectionTitle.SIVI]: {
            ...this.searchData?.detail.sivi,
            accordionData: siviMappedResponse,
            dropDownData: {
              siviUltimo: finalResponse,
              siviDropDownData: listingResponse,
              searchParams: {
                formIdVeic: this.searchForm.getRawValue()['plateType'],
                idVeic: `${
                  this.searchForm.getRawValue()['plate'] || 'GF500YV'
                }`,
                codTpVeic:
                  NO_FILTER.includes(this.searchForm.getRawValue()['vehicleType']!)
                    ? ''
                    : this.searchForm.getRawValue()['vehicleType'],
                dataRiferimento: finalResponse.dataEmissione,
              },
            },
          },
        },
      } as ISectionData;
    }else{
      this.searchData = {
        summary: {},
        detail: {},
      } as ISectionData;

      if (this.searchData) {
        this.searchData!.summary.sivi = this.utilityService.buildSummaryData(
          siviSectionSummary.data,
          emptySivi
        ) as ISiviSummaryMappedResponse;
        this.searchData!.detail.sivi = {
          accordionData: {} as ISiviDetailMappedResponse,
          dropDownData: {} as ISiviDropdown,
        };
      }
    }
  }

  private buildSitaData(
    sitaListResponse: ISitaListingResponse | undefined,
    sitaFinalResponse: ISitaLastResponse | undefined
  ): void {
    let listingResponse = sitaListResponse as any;
    const finalResponse = sitaFinalResponse as any;
    if(!listingResponse && !finalResponse){
       return;
      }
    if (!(finalResponse as IErrorResponse).error) {
      const datesPropsToConvert = [
        'dataIncasso',
        'dataLastAgg',
        'dataScadMora',
        'dataScadSosp',
        'effettoCopertura',
        'effettoPolizza',
        'scadenzaCopertura',
        'scadenzaPolizza',
      ];
      const hourPropsToConvert = ['oraScadCop', 'oraEffCop'];
      const formData = this.searchForm.getRawValue();
      this.utilityService.changePlateLabelIfPlateTypeIsChassis(sitaCoverageDetailsRows, formData)
      const sitaMappedResponse = this.utilityService.mapCodeToValue({
        ...finalResponse,
        ...this.convertDataFormat(finalResponse, datesPropsToConvert, 'date'),
        ...this.convertDataFormat(
          finalResponse,
          hourPropsToConvert,
          'stringHour'
        ),
        targa: this.utilityService.setIdVeic(formData),
        ggesteCop: finalResponse.ggesteCop ? parseInt(finalResponse.ggesteCop) : '',
        impresaDbLabel: finalResponse.impresaDb,
        tipoVeicoloLabel: finalResponse.tipoVeicolo,
        tipoVeicoloMCTCLabel: finalResponse.tipoVeicoloMCTC,
        canaleAggLabel: finalResponse.canaleAgg,
        activity: this.translate.instant('DASHBOARD.DETAILED-DATA.ACTIVITY'),
        tipoPolizza: finalResponse.tipoPolizza,
        blackBox: finalResponse.blackBox === '1' ? 'Presente' : 'Assente',
        datiCompleti: finalResponse.datiCompleti === 'G' ? 'No' : 'Si',
        timeLastAgg: this.utilityService.convertStringTimestampToDate(
          finalResponse.timeLastAgg || '',
          true
        ),
      });
      sitaMappedResponse.impresaDb = sitaMappedResponse.impresaDb
        ? sitaMappedResponse.impresaDb +
          ' - ' +
          sitaMappedResponse.impresaDbLabel
        : '';
      sitaMappedResponse.tipoVeicolo = sitaMappedResponse.tipoVeicolo
        ? sitaMappedResponse.tipoVeicolo +
          ' - ' +
          sitaMappedResponse.tipoVeicoloLabel
        : '';
      sitaMappedResponse.tipoVeicoloMCTC = sitaMappedResponse.tipoVeicoloMCTC
        ? sitaMappedResponse.tipoVeicoloMCTC +
          ' - ' +
          sitaMappedResponse.tipoVeicoloMCTCLabel
        : '';
      sitaMappedResponse.canaleAgg = sitaMappedResponse.canaleAgg
        ? sitaMappedResponse.canaleAgg +
          ' - ' +
          sitaMappedResponse.canaleAggLabel
        : '';
        sitaMappedResponse.tipoMov = sitaMappedResponse.tipoMov && sitaMappedResponse.causMov
        ? sitaMappedResponse.tipoMov +
          ' - ' +
          sitaMappedResponse.causMov
        : sitaMappedResponse.tipoMov || sitaMappedResponse.causMov ? sitaMappedResponse.tipoMov || sitaMappedResponse.causMov : '';
      this.sitaMappedResponse = sitaMappedResponse;
      listingResponse = {
        coperture: listingResponse.coperture,
      };
      this.searchData = {
        summary: {
          ...this.searchData?.summary,
          [ESectionTitle.SITA]: this.utilityService.buildSummaryData(
            sitaSectionSummary.data,
            sitaMappedResponse
          ) as ISitaSummaryMappedResponse,
        },
        detail: {
          ...this.searchData?.detail,
          [ESectionTitle.SITA]: {
            accordionData: {
              ...sitaMappedResponse,
              tipoVeicoloOriginal:finalResponse.tipoVeicolo,
              impresaDbOriginal: finalResponse.impresaDb,
              tipoVeicoloMCTCOriginal: finalResponse.tipoVeicoloMCTC,
              canaleAggOriginal: finalResponse.canaleAgg
            },
            dropDownData: {
              sitaUltimo: finalResponse,
              sitaDropDownData: listingResponse,
              searchParams: {
                formIdVeic: this.searchForm.getRawValue()['plateType'],
                idVeic: `${
                  this.searchForm.getRawValue()['plate'] || 'GF500YV'
                }`,
                tipoVeicolo:
                  NO_FILTER.includes(this.searchForm.getRawValue()['vehicleType']!)
                    ? ''
                    : this.searchForm.getRawValue()['vehicleType'],
                dataRiferimento: finalResponse.effettoCopertura,
                oraRiferimento: '0000',
              },
            },
          },
        },
      } as ISectionData;
    }else{
      if (this.searchData) {
        this.searchData!.summary.sita = this.utilityService.buildSummaryData(
          sitaSectionSummary.data,
          emptySita
        ) as ISitaSummaryMappedResponse;
        this.searchData!.detail.sita = {
          accordionData: {} as ISitaLastMappedResponse,
          dropDownData: {} as ISitaDropdown,
        };
      }
    }
  }

  private buildAtrcData(
    atrcListResponse: IAtrcListingResponse | undefined,
    atrcFinalResponse: IAtrcDetailResponse | undefined
  ): void {
    const listingResponse = atrcListResponse as any;
    const finalResponse = atrcFinalResponse as any;
    if(!listingResponse && !finalResponse){
      return;
    }
    if (!(finalResponse as IErrorResponse).error) {
      const datesPropsToConvert = [
        'dataScadContratto',
        'dataFlussoIns',
        'dataFlussoAgg',
        'dataElab',
      ];
      const timestampsToConvert = ['timestampIns', 'timestampAgg'];
      const formData = this.searchForm.getRawValue();
      this.utilityService.changePlateLabelIfPlateTypeIsChassis(atrcSectionRows, formData)
      const atrcMappedResponse = this.utilityService.mapCodeToValue({
        ...finalResponse,
        ...this.convertDataFormat(finalResponse, datesPropsToConvert, 'date'),
        ...this.convertDataFormat(
          finalResponse,
          timestampsToConvert,
          'timestamp',
          true
        ),
        targa: this.utilityService.setIdVeic(formData),
        tipoVeicoloLabel: finalResponse.tipoVeicolo,
        codImpCTPLabel: finalResponse.codImpCTP ? finalResponse.codImpCTP.toString() : '',
        numSinistriRC5Anni: this.getNumSinistriUltimiAnni(finalResponse, 5),
        numSinistriRC2Anni: this.getNumSinistriUltimiAnni(finalResponse, 2),
      });
      this.searchData = {
        summary: {
          ...this.searchData?.summary,
          [ESectionTitle.ATRC]: this.utilityService.buildSummaryData(
            atrcSectionSummary.data,
            atrcMappedResponse
          ) as IAtrcSummaryMappedResponse,
        },
        detail: {
          ...this.searchData?.detail,
          [ESectionTitle.ATRC]: {
            accordionData: atrcMappedResponse,
            dropDownData: {
              atrcUltimo: finalResponse,
              atrcDropDownData: listingResponse,
              searchParams: {
                formIdVeic: this.searchForm.getRawValue()['plateType'],
                idVeic: `${
                  this.searchForm.getRawValue()['plate'] || 'GF500YV'
                }`,
                tipoVeicolo:
                  NO_FILTER.includes(this.searchForm.getRawValue()['vehicleType']!)
                    ? ''
                    : this.searchForm.getRawValue()['vehicleType'],
                dataRiferimento: finalResponse.dataScadContratto,
                oraRiferimento: '0000',
              },
            },
          },
        },
      } as ISectionData;
    }else{
      if (this.searchData) {
        this.searchData!.summary.atrc = this.utilityService.buildSummaryData(
          atrcSectionSummary.data,
          emptyAtrc
        ) as IAtrcSummaryMappedResponse;
        this.searchData!.detail.atrc = {
          accordionData: {} as IAtrcDetailMappedResponse,
          dropDownData: {} as IAtrcDropdown,
        };
      }
    }
  }

  setErrorMessage(config: IInputConfig): void {
    const control = config.form.get(config.formControlName);
    if (!control || !control.errors) {
      config.errorMessage = ''
      return;
    }

    if (control.errors['required']) {
      config.errorMessage = this.translate.instant('ERRORS.MANDATORY-PLATE');
    } else if (control.errors['invalid_license']) {
      config.errorMessage =  this.translate.instant('ERRORS.REMOVE-SPECIAL-CHAR');
    }
    this.cdr.detectChanges();
  }
}
